/* eslint-disable no-console */
import React, { memo, useCallback } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import { tagular } from "@cohesion/tagular";

import { keyify } from "@utils/keyify";
import { useBestInTravel } from "@context/bestInTravel/2025/bestInTravelContext";

import { MapPinFill } from "@icons";
import { Arrow } from "@icons/elsewhere";

export const Marker = memo(
  ({
    themeClasses = { border: "", background: "", text: "" },
    name,
    index = undefined,
    inactiveMarker = false,
    category,
  }) => {
    const { markerId, setMarkerId, screen } = useBestInTravel();
    const showMarkerLabel = markerId === index;

    const handleMouseEnter = useCallback(
      () => setMarkerId(index),
      [index, setMarkerId]
    );

    const handleMouseLeave = useCallback(
      () => setMarkerId(null),
      [setMarkerId]
    );

    const handleClick = useCallback(() => {
      if (screen === "sm") {
        setMarkerId(index);
      }

      if (showMarkerLabel) {
        const newHash = `#${keyify(name)}`;
        window.location.hash = "";
        window.location.hash = newHash;
        tagular("click", {
          actionOutcome: "INTERNALLINK",
          outboundUrl: newHash,
          webElement: {
            location: `BIT_Map_${category}`,
            elementType: "LINK",
            name,
            position: "0",
          },
        });
      }
    }, [showMarkerLabel, screen, name, index, setMarkerId, category]);

    return (
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={`Location marker for ${name}`}
        className={cn(
          "border rounded-pill p-2 font-semibold text-sm flex gap-2 items-center w-content -translate-x-1/2 -translate-y-full",
          showMarkerLabel && "z-10 relative",
          inactiveMarker && "text-[#B7B7B7] bg-[#E7E7E7] pointer-events-none",
          themeClasses.text,
          themeClasses.background,
          themeClasses.border
        )}
      >
        <MapPinFill className={cn("flex w-4 h-4", themeClasses.text)} />

        {showMarkerLabel && (
          <div className="flex items-center gap-2 transition-all duration-300 ease-in-out">
            <span className="font-semibold leading-none font-neue whitespace-nowrap">
              {name}
            </span>
            <Arrow className={cn("w-4 h-4", themeClasses.text)} />
          </div>
        )}
      </button>
    );
  }
);

Marker.propTypes = {
  name: PropTypes.string.isRequired,
  themeClasses: PropTypes.shape({
    border: PropTypes.string.isRequired,
    background: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }),
  index: PropTypes.number,
  inactiveMarker: PropTypes.bool,
  category: PropTypes.string.isRequired,
};
